"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { trackEvent } from "@/utils/gtm";
import WhatsAppButton from "@/components/WhatsApp/WhatsAppButton";
import { whatsappMessages } from "@/utils/whatsapp";
import EnrollButton from "@/components/LeadForm/EnrollButton";
import { FORM_CONTEXTS } from "@/utils/innovlistWidget";

const slides = [
    {
        headline: "Your IT Career Starts with the Right Skills",
        subheadline: "Live online training from 1,000+ certified industry experts. Learn at your pace and land the career you deserve.",
        btn1: { label: "Explore Courses", href: "/courses" },
        btn2: { label: "Talk to an Advisor", href: "/contact" },
        backgroundImage: "https://seasoftclasses.com/images/hero-img1.webp",
        alt: "Student learning IT skills on laptop with online instructor guidance",
        // Crucial SEO Fixes for the first slide:
        priority: true, 
        fetchPriority: "high",
        loading: "eager", 
    },
    {
        headline: "Learn Skills That Shape Your Future",
        subheadline: "From Power BI to Salesforce, SAS to Cybersecurity — master the tools that top employers are hiring for right now.",
        btn1: { label: "View All Courses", href: "/courses" },
        btn2: { label: "Talk to an Advisor", href: "/contact-us" },
        backgroundImage: "https://seasoftclasses.com/images/hero-img2.webp",
        alt: "Data dashboards for Power BI and Salesforce training modules",
        // Slides 2 and 3 can stay lazy because they are hidden at start
        priority: false,
        fetchPriority: "low",
        loading: "lazy",
    },
    {
        headline: "Get Certified. Get Hired. Get Ahead.",
        subheadline: "Join thousands of IT professionals who launched their careers with Seasoft Classes. Placement support included.",
        btn1: { label: "Enroll Now", href: "/courses" },
        btn2: { label: "Talk to an Advisor", href: "/contact-us" },
        backgroundImage: "https://seasoftclasses.com/images/hero-img3.webp",
        alt: "Certified IT professionals showcasing career advancement milestones",
        priority: false,
        fetchPriority: "low",
        loading: "lazy",
    },
];

const heroStats = [
    {
        icon: (
            <svg
                aria-hidden="true"
                className="h-4 w-4 text-yellow-300"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M8 21h8" />
                <path d="M12 17v4" />
                <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
                <path d="M17 5h3v2a4 4 0 0 1-4 4" />
                <path d="M7 5H4v2a4 4 0 0 0 4 4" />
            </svg>
        ),
        label: "Top Rated",
    },
    { label: "1,000+ Instructors" },
    { label: "300+ Courses" },
    { label: "14,000+ Batches" },
    { label: "10+ Years" },
];

export function Hero() {
    const pathname = usePathname();
    const currentPage = pathname || "/";
    const [activeSlide, setActiveSlide] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveSlide((prev) => (prev + 1) % slides.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const goTo = (idx: number) => setActiveSlide(idx);
    const prev = () => setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);
    const next = () => setActiveSlide((prev) => (prev + 1) % slides.length);

    return (
        <>
            {/* HERO V1 — HIDDEN, re-enable by removing hidden class */}
            <section className="hidden bg-brand-blue-light py-20 overflow-hidden">
            <div className="max-w-[1400px] mx-auto px-4 lg:px-8 flex flex-col lg:flex-row items-center gap-16">
                {/* Left Content */}
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    className="flex-1 text-center lg:text-left"
                >
                    <h1 className="text-4xl lg:text-5xl font-extrabold text-[#002147] leading-tight mb-6">
                        Your IT Career Starts <br />
                        with the Right Skills
                    </h1>
                    <p className="text-gray-600 text-lg mb-10 max-w-xl">
                        Live online training from 1,000+ certified industry experts. Learn at your pace, get job-ready, and land the career you deserve.
                    </p>

                    <div className="max-w-xl">
                        <Link
                            href="/contact-us"
                            onClick={() => trackEvent("Lead", "click", "counseling_cta", currentPage)}
                            className="inline-block bg-blue-600 text-white px-8 py-4 rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-md"
                        >
                            Enquire Now
                        </Link>
                    </div>

                    <div className="mt-12 flex items-center gap-12 justify-center lg:justify-start">
                        <div className="flex items-center gap-3">
                            <div className="bg-yellow-400 p-2 rounded-lg">
                                <span className="text-[#002147] font-bold text-xl">4.8</span>
                            </div>
                            <div>
                                <p className="text-[#002147] font-bold text-xl">Top Rated</p>
                                <p className="text-gray-500 text-sm">Course Content</p>
                            </div>
                        </div>
                        <div className="border-l border-gray-300 h-10 hidden sm:block"></div>
                        <div>
                            <p className="text-[#002147] font-bold text-xl">20+</p>
                            <p className="text-gray-500 text-sm">Expert Mentors</p>
                        </div>
                    </div>
                </motion.div>

                {/* Right Images */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8 }}
                    className="flex-1 relative w-full max-w-2xl"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                        {/* Main Image */}
                        <div className="relative h-[560px] rounded-2xl overflow-hidden shadow-2xl">
                            <Image
                                src="/images/hero1.jpg"
                                alt="Learning Group"
                                fill
                                className="object-cover"
                                loading="lazy"
                            />
                            {/* Student Count Badge - Inside Image */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5, duration: 0.5 }}
                                className="absolute bottom-10 left-4 right-4 md:right-auto"
                            >
                                <div className="bg-white px-4 py-2 rounded-3xl shadow-xl inline-flex items-center gap-2">
                                    <span className="text-[#002147] font-extrabold text-sm">10K +</span>
                                    <span className="text-[#002147] font-semibold text-sm">Active Student</span>
                                </div>
                            </motion.div>
                        </div>

                        {/* Side Images Container */}
                        <div className="flex flex-col gap-6">
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.3, duration: 0.6 }}
                                className="relative h-[270px] rounded-2xl overflow-hidden shadow-xl"
                            >
                                <Image
                                    src="/images/hero2.jpg"
                                    alt="Student 1"
                                    fill
                                    className="object-cover"
                                    loading="lazy"
                                />
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.5, duration: 0.6 }}
                                className="relative h-[270px] rounded-2xl overflow-hidden shadow-xl"
                            >
                                <Image
                                    src="/images/hero3.jpg"
                                    alt="Student 2"
                                    fill
                                    className="object-cover"
                                    loading="lazy"
                                />
                            </motion.div>
                        </div>
                    </div>
                </motion.div>
            </div>
            </section>

            {/* HERO V2 — ACTIVE. To switch heroes,
                remove hidden from V1 and add hidden to V2 */}
            <section className="relative w-full min-h-[90vh] flex flex-col bg-gradient-to-br from-[#0a1628] to-[#1a3a6b] overflow-hidden">
                {/* Slides */}
                <div className="relative flex-1 flex items-center">
                    {slides.map((slide, idx) => (
                        <motion.div
                            key={idx}
                            initial={false}
                            animate={{ opacity: activeSlide === idx ? 1 : 0 }}
                            transition={{ duration: 0.6 }}
                            className={`absolute inset-0 flex items-center ${
                                activeSlide === idx
                                    ? "pointer-events-auto"
                                    : "pointer-events-none"
                            }`}
                        >
                            {/* Hero Background Image - LCP Optimized */}
                            <Image
                                src={slide.backgroundImage}
                                alt={slide.headline}
                                fill
                                priority={idx === 0}
                                fetchPriority={idx === 0 ? "high" : "auto"}
                                sizes="100vw"
                                quality={85}
                                className="object-cover"
                            />

                            {/* Overlay */}
                            <div className="absolute inset-0 bg-[#081426]/70" />
                            <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-20 w-full relative z-10">
                                <div className="relative z-10 max-w-3xl text-center mx-auto lg:text-left lg:mx-0">
                                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-6">
                                        {slide.headline}
                                    </h1>
                                    <p className="text-white/80 text-lg mb-10 max-w-2xl">
                                        {slide.subheadline}
                                    </p>
                                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                                        {slide.btn1.label === "Enroll Now" ? (
                                            <EnrollButton
                                                label={slide.btn1.label}
                                                variant="primary"
                                                size="lg"
                                                context={FORM_CONTEXTS.ENROLL_NOW}
                                                className="!text-sm sm:!text-base !px-5 sm:!px-8 !py-2.5 sm:!py-4"
                                            />
                                        ) : (
                                            <Link
                                                href={slide.btn1.href}
                                                onClick={() =>
                                                    trackEvent("Navigation", "hero_primary_cta", slide.btn1.label, currentPage)
                                                }
                                                className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200 text-sm sm:text-lg px-5 sm:px-8 py-2.5 sm:py-4"
                                            >
                                                {slide.btn1.label}
                                                <span className="ml-2">-&gt;</span>
                                            </Link>
                                        )}
                                        <EnrollButton
                                            label={slide.btn2.label}
                                            variant="white"
                                            size="lg"
                                            context={FORM_CONTEXTS.COUNSELING}
                                            className="!text-sm sm:!text-base !px-5 sm:!px-8 !py-2.5 sm:!py-4"
                                        />
                                        <WhatsAppButton
                                            variant="hero-outline"
                                            size="lg"
                                            label="WhatsApp Us"
                                            message={whatsappMessages.counseling}
                                            context="hero_slide"
                                            className="!text-sm sm:!text-base !px-5 sm:!px-8 !py-2.5 sm:!py-4 border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-slate-950 hover:!bg-slate-950 hover:!text-white transition-colors"
                                        />
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}

                    {/* Prev arrow */}
                    <button 
                        type="button"
                        onClick={prev}
                        aria-label="Previous slide"
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 group cursor-pointer hidden sm:flex"
                    >
                        <div className="w-12 h-12 rounded-full bg-white/20 group-hover:bg-white/40 transition-colors flex items-center justify-center text-white">
                            <svg 
                                width="20" 
                                height="20" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2.5" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <polyline points="15 18 9 12 15 6"/>
                            </svg>
                        </div>
                    </button>

                    {/* Next arrow */}
                    <button 
                        type="button"
                        onClick={next}
                        aria-label="Next slide"
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 group cursor-pointer hidden sm:flex"
                    >
                        <div className="w-12 h-12 rounded-full bg-white/20 group-hover:bg-white/40 transition-colors flex items-center justify-center text-white">
                            <svg 
                                width="20" 
                                height="20" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2.5" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <polyline points="9 18 15 12 9 6"/>
                            </svg>
                        </div>
                    </button>
                </div>

                {/* Dot indicators */}
                <div className="absolute bottom-[5.5rem] left-0 right-0 flex justify-center gap-3 z-10">
                    {slides.map((_, idx) => (
                        <button
                            key={idx}
                            type="button" // Ensuring standard button behavior
                            onClick={() => goTo(idx)}
                            aria-label={`Go to slide ${idx + 1}`}
                            aria-current={activeSlide === idx ? "true" : "false"} // Dynamic screen-reader state
                            className="relative cursor-pointer group" // Invisible padding wrapper + group target
                            >
                            {/* This is your original visual circle element */}
                            <div
                                className={`w-3 h-3 rounded-full border-2 border-white transition-all ${
                                activeSlide === idx 
                                    ? "bg-white" 
                                    : "bg-transparent group-hover:bg-white/20" // Replaced standard hover with a group hover
                                }`}
                            />
                        </button>
                    ))}
                </div>

                {/* Stats bar */}
                <div className="relative z-10 bg-white/10 border-t border-white/20 py-4 px-4">
                    <div className="max-w-[1400px] mx-auto flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-white text-sm font-semibold">
                        {heroStats.map((stat, idx) => (
                            <span key={idx} className="flex items-center gap-2">
                                {stat.icon && <span>{stat.icon}</span>}
                                <span>{stat.label}</span>
                                {idx < heroStats.length - 1 && (
                                    <span className="hidden sm:inline text-white/40 ml-6">|</span>
                                )}
                            </span>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
